import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSession } from '../context/SessionContext.jsx';
import { continuityLineFor } from '../util/authProvider.js';
import { etTodayLabel } from '../util/etDate.js';
import { latestResolvedAtMillis } from '../data/results.js';
import { lastViewedMillis } from '../util/resultsBadge.js';
import { getScreenCache } from '../util/screenCache.js';
import { db } from '../firebase.js';
import SaveAccountModal from './SaveAccountModal.jsx';
import { installDismissed, dismissInstall, recordPwaInstall } from '../util/pwaInstall.js';
import { isIOS, isStandalone } from '../util/platform.js';
import { appStoreUrl } from '../util/appStore.js';
import { logWebEvent } from '../firebase.js';

// App chrome: brand+date header, a friendly wall if the session can't start,
// and a bottom tab bar (Today + Results). The bar is HIDDEN on /q/:id so the
// single-question share stays a focused, chromeless door.
export default function AppShell({ children }) {
  const { status, errorCode, uid, isAnonymous, appCheckActive } = useSession();
  const { pathname, search } = useLocation();
  const [latestResolved, setLatestResolved] = useState(0);
  // Server-written users/{uid}.lastResultsViewedAt (millis) for secured accounts
  // — reconciled with the local value so a result viewed on another device
  // clears the badge here too (audit B9). 0 for anon / no doc.
  const [serverViewedMillis, setServerViewedMillis] = useState(0);
  // Whether the signed-in account is an iOS-app player — true if it was born on
  // the app (platform:'ios') OR has opened the app at least once
  // (appDownloadBonusAt, the +180 install anchor, stamped on first app launch
  // regardless of where the account started). The latter "graduates" a user who
  // began on web and later installed the app. (A guest's anon web session has
  // neither and isn't linked to a fresh install — that orphan is undetectable.)
  const [isAppAccount, setIsAppAccount] = useState(false);

  // One cheap read of the newest resolvedAt — drives the Results "new" dot.
  useEffect(() => {
    let cancelled = false;
    if (status === 'ready' && uid) {
      latestResolvedAtMillis(uid)
        .then((m) => !cancelled && setLatestResolved(m))
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [status, uid]);

  // If this session is running AS the installed PWA (launched from the home
  // screen), record the web→web-app conversion. The durable counterpart to the
  // `appinstalled` event (which only fires at the install moment): this catches
  // devices that installed before the marker shipped, on their next open.
  // recordPwaInstall is idempotent + device-locally guarded, so it hits the
  // backend at most once per device regardless of how many times it's called.
  useEffect(() => {
    if (isStandalone()) recordPwaInstall('standalone_launch');
  }, []);

  // Read the signed-in account doc once (secured users only — anonymous guests
  // are always web-born and never linked to an install). Cheap single read.
  useEffect(() => {
    let cancelled = false;
    if (status === 'ready' && uid && !isAnonymous) {
      // Prefer the profile the Profile tab already fetched (audit X2) — avoids a
      // duplicate users/{uid} read when it's cached; fall back to a direct read.
      const cached = getScreenCache(`profile:${uid}`);
      if (cached) {
        setIsAppAccount(cached.platform === 'ios' || !!cached.appDownloadBonusAt);
        setServerViewedMillis(cached.lastResultsViewedAtMillis ?? 0);
      } else {
        db.collection('users')
          .doc(uid)
          .get()
          .then((s) => {
            if (cancelled) return;
            const d = s.exists ? s.data() : {};
            setIsAppAccount(d.platform === 'ios' || !!d.appDownloadBonusAt);
            setServerViewedMillis(d.lastResultsViewedAt?.toMillis?.() ?? 0);
          })
          .catch(() => {});
      }
    } else {
      setIsAppAccount(false);
      setServerViewedMillis(0);
    }
    return () => {
      cancelled = true;
    };
  }, [status, uid, isAnonymous]);

  const onQuestionDoor = pathname.startsWith('/q/');
  // The invite/onboarding doors — nothing should distract from the preferred
  // accept → save → play arc, so the generic install banner is suppressed on
  // these routes AND on Today while an onboarding is still active (Tom
  // 2026-07-18, item #1). `onboardingActive` mirrors Play.jsx's
  // `readActiveMatchup` TTL check (ps_active_matchup, 24h) and also treats a
  // fresh `?f=` predictor-share landing as active. The banner still shows on
  // the other tabs (Stats/Games/Results/Profile) during onboarding.
  const onInviteDoor = pathname.startsWith('/i/') || pathname.startsWith('/f/');
  const isTodayTab = pathname === '/' || pathname === '/play';
  const onboardingActive = (() => {
    if (isTodayTab && new URLSearchParams(search).get('f')) return true;
    try {
      const raw = localStorage.getItem('ps_active_matchup');
      if (!raw) return false;
      const m = JSON.parse(raw);
      return !!m?.at && Date.now() - m.at <= 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  })();
  const suppressInstallBanner =
    onQuestionDoor || onInviteDoor || (isTodayTab && onboardingActive);
  // If App Check never activated, every Firestore/Functions call 401s — show the
  // friendly wall rather than a broken screen (audit B17: 'unavailable' status
  // alone doesn't cover this, since App Check failure doesn't throw on sign-in).
  const sessionWalled = status === 'unavailable' || !appCheckActive;
  const showNav = status === 'ready' && !onQuestionDoor && !sessionWalled;
  // Dot shows when something resolved since last viewed — but never while
  // you're already on Results (visiting it clears the badge). Reconcile the
  // local last-viewed with the server value so it's correct cross-device (B9).
  const resultsHasNew =
    pathname !== '/results' &&
    latestResolved > 0 &&
    latestResolved > Math.max(lastViewedMillis(uid), serverViewedMillis);

  return (
    <div>
      {/* App-nudge banners (hidden on the /q door so the share stays chromeless).
          If the signed-in account is an iOS-app player (born on iOS OR has
          opened the app — i.e. graduated from web), show the specific "your
          account lives in the app" nudge; otherwise fall back to the generic
          iPhone-Safari nudge. Never a hard block — can't detect installs for
          the anonymous-guest case, and a block would also catch prospects. */}
      {!onQuestionDoor &&
        (isAppAccount ? (
          <IOSOriginNudge />
        ) : (
          // Suppress the generic install banner on /profile — that tab already
          // has its own richer "Get the app" conversion block, so two stacked
          // CTAs would be redundant (audit C10) — and on the invite/onboarding
          // doors so the accept→save→play arc isn't cluttered (item #1).
          !suppressInstallBanner && pathname !== '/profile' && <IOSAppBanner />
        ))}

      <header
        style={{
          maxWidth: 'var(--maxw)',
          margin: '0 auto',
          padding: '18px 16px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <Link
            to="/play"
            style={{
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: '-0.02em',
              color: 'var(--primary-text)',
            }}
          >
            Predict<span style={{ color: 'var(--gold)' }}> Six</span>
          </Link>
          {/* Second line under the brand: a per-route title — "Today's
              Questions" on Today, "Results" on Results, etc. — in the date's slot. */}
          <span
            style={{
              color: 'var(--primary-text)',
              fontWeight: 900,
              fontSize: 26,
              letterSpacing: '-0.02em',
              whiteSpace: 'nowrap',
            }}
          >
            {PAGE_TITLES[pathname] || etTodayLabel()}
          </span>
        </div>
      </header>

      {sessionWalled ? <Unavailable errorCode={errorCode} /> : children}

      {showNav && <BottomNav pathname={pathname} resultsHasNew={resultsHasNew} />}

      <SaveAccountModal />
    </div>
  );
}

// True for an iPhone/iPad Safari visitor who is NOT already running the
// installed PWA (no point nudging if they've added it to the Home Screen).
// We can't detect whether the native app is installed — so this is a soft
// nudge, never a block. Device detection comes from the shared util/platform.js
// so it stays in lockstep with Play/Profile.
function isIOSWeb() {
  return isIOS() && !isStandalone();
}

// Dismissible "get the iOS app" banner for iPhone web visitors. Leads with the
// save-first message so an existing web player doesn't install fresh and orphan
// their picks/coins (the real risk — we don't hard-disable web play).
function IOSAppBanner() {
  const { isAnonymous, openSaveAccount } = useSession();
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('ps_ios_banner_dismissed') === '1'
  );
  // Hide when already added to the Home Screen (isIOSWeb checks !standalone) OR
  // dismissed — here OR from any other install prompt (shared flag, 14d TTL).
  const show = isIOSWeb();
  if (!show || dismissed || installDismissed()) return null;
  const close = () => {
    try {
      localStorage.setItem('ps_ios_banner_dismissed', '1');
    } catch {
      /* private mode — just hide for this session */
    }
    dismissInstall(); // also silence the in-card install/A2HS CTAs app-wide
    setDismissed(true);
  };
  // Anonymous "Get" routes through the same save-gate Play's locked card built
  // (audit R13): the banner copy SAYS "save your account first," but the button
  // used to send them straight to the store, orphaning this browser's picks.
  const openStore = () => window.open(appStoreUrl('web_banner'), '_blank', 'noopener');
  return (
    <div style={iosBannerWrap}>
      <img src="/icon-192.png" alt="" width={40} height={40} style={{ borderRadius: 9, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>Get the Predict Six app</div>
        <div style={{ fontSize: 12, color: 'var(--secondary-text)', lineHeight: 1.35 }}>
          {/* C2 (Tom 2026-07-07): "groups & challenges" went stale once web
              shipped both — the honest app-only pitch on iPhone is push
              notifications that actually arrive and the full reveal show.
              Teams mention dropped 2026-07-08 (Tom paused the feature). */}
          Result alerts &amp; the full reveal show — save your account first so your picks &amp; coins come with you.
        </div>
      </div>
      {isAnonymous ? (
        <button
          onClick={() => {
            logWebEvent('web_install_cta_tapped', { platform: 'ios', anon: true, source: 'banner' });
            openSaveAccount({
              reason: 'install_gate',
              onContinue: openStore,
              continueLabel: 'Continue to the App Store',
              skipLabel: 'No thanks — take me to the App Store',
            });
          }}
          style={{ ...iosBannerGet, border: 'none', cursor: 'pointer' }}
        >
          Get
        </button>
      ) : (
        <a
          href={appStoreUrl('web_banner')}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logWebEvent('web_install_cta_tapped', { platform: 'ios', anon: false, source: 'banner' })}
          style={iosBannerGet}
        >
          Get
        </a>
      )}
      <button onClick={close} aria-label="Dismiss" style={iosBannerClose}>
        ✕
      </button>
    </div>
  );
}

// Soft nudge for a logged-in web session whose account was born on the iOS app
// (platform:'ios'). The one case where web can KNOW you're an app player — so we
// point you back to the app (where your activity + metrics live) without
// blocking web. Account-based, so it shows on any device; dismissible.
function IOSOriginNudge() {
  const { providerId } = useSession();
  const [dismissed, setDismissed] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('ps_ios_origin_dismissed') === '1'
  );
  if (dismissed) return null;
  const close = () => {
    try {
      localStorage.setItem('ps_ios_origin_dismissed', '1');
    } catch {
      /* private mode — hide for this session */
    }
    setDismissed(true);
  };
  // The App Store link is only actionable on an actual iPhone/iPad — this nudge
  // is account-origin-based, so it can also fire on Android/desktop (e.g. the
  // user opened web on a laptop). There, "Open" → the App Store is a dead end,
  // so drop the CTA and keep just the "open it on your phone" line.
  const onIOS = isIOS();
  return (
    <div style={iosBannerWrap}>
      <img src="/icon-192.png" alt="" width={40} height={40} style={{ borderRadius: 9, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 14 }}>Your account lives in the app</div>
        <div style={{ fontSize: 12, color: 'var(--secondary-text)', lineHeight: 1.35 }}>
          Open Predict Six on your phone for groups, challenges &amp; your full stats — that's where
          your play is tracked.
        </div>
        {/* OB-2 (Tom 2026-07-16): this banner exists precisely because the
            account came from web — so it's the one place a provider mismatch
            is most likely and most costly. Name the button they need to tap.
            See docs/onboarding-experience-2026-07.md §6. */}
        {continuityLineFor(providerId) && (
          <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--link-gold)', lineHeight: 1.35, marginTop: 3 }}>
            {continuityLineFor(providerId)}
          </div>
        )}
      </div>
      {onIOS && (
        <a href={appStoreUrl('web_origin_nudge')} target="_blank" rel="noopener noreferrer" style={iosBannerGet}>
          Open
        </a>
      )}
      <button onClick={close} aria-label="Dismiss" style={iosBannerClose}>
        ✕
      </button>
    </div>
  );
}

// Floating pill nav (mirrors the iOS app's tab bar): a rounded elevated bar
// centered at the bottom, icon-over-label, the active tab in an oval highlight
// + gold. Five surfaces: Today · Stories · Games · Results · Profile. STORIES
// took the Stats slot (SO-4, 2026-08-14): the app is a news app whose
// questions are chapters of running stories, and the tab bar should point at
// what the product IS. Stats did not die — its cards live on Profile, which
// is where iOS already keeps them. Hidden on
// /q/:id so the single-question share stays a focused, chromeless door.
function BottomNav({ pathname, resultsHasNew }) {
  const isToday = pathname === '/' || pathname === '/play';
  return (
    <nav style={navWrap}>
      <div style={navPill}>
        <TabLink to="/play" label="Today" active={isToday} icon={<SunIcon />} />
        <TabLink to="/stories" label="Stories" active={pathname === '/stories'} icon={<NewsIcon />} />
        <TabLink to="/groups" label="Games" active={pathname === '/groups'} icon={<GroupsIcon />} />
        <TabLink
          to="/results"
          label="Results"
          active={pathname === '/results'}
          dot={resultsHasNew}
          icon={<ClipboardIcon />}
        />
        <TabLink
          to="/profile"
          label="Profile"
          active={pathname === '/profile'}
          icon={<PersonIcon />}
          // Re-tapping Profile while already on it pops any open sub-page
          // (About / Terms / Privacy) back to the main profile root.
          onReTap={() => window.dispatchEvent(new CustomEvent('ps-profile-reset'))}
        />
      </div>
    </nav>
  );
}

function TabLink({ to, label, active, dot, icon, onReTap }) {
  return (
    <Link
      to={to}
      aria-current={active ? 'page' : undefined}
      onClick={() => { if (active && onReTap) onReTap(); }}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        padding: '7px 9px',
        borderRadius: 18,
        background: active ? 'var(--selected)' : 'transparent',
        color: active ? 'var(--gold)' : 'var(--primary-text)',
        textDecoration: 'none',
        transition: 'background 150ms, color 150ms',
      }}
    >
      <span style={{ display: 'inline-flex', position: 'relative' }}>
        {icon}
        {dot && (
          <span
            aria-label="new"
            style={{
              position: 'absolute',
              top: -2,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: 4,
              background: 'var(--gold)',
            }}
          />
        )}
      </span>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.01em' }}>
        {label}
      </span>
    </Link>
  );
}

function SunIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4.2" fill="currentColor" stroke="none" />
      <line x1="12" y1="1.6" x2="12" y2="3.9" />
      <line x1="12" y1="20.1" x2="12" y2="22.4" />
      <line x1="1.6" y1="12" x2="3.9" y2="12" />
      <line x1="20.1" y1="12" x2="22.4" y2="12" />
      <line x1="4.5" y1="4.5" x2="6.1" y2="6.1" />
      <line x1="17.9" y1="17.9" x2="19.5" y2="19.5" />
      <line x1="4.5" y1="19.5" x2="6.1" y2="17.9" />
      <line x1="17.9" y1="6.1" x2="19.5" y2="4.5" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4.5" y="4.8" width="15" height="16.2" rx="2.5" />
      <rect x="9" y="2.6" width="6" height="3.6" rx="1.5" fill="currentColor" stroke="none" />
      <line x1="8.6" y1="11.2" x2="15.4" y2="11.2" />
      <line x1="8.6" y1="14.6" x2="15.4" y2="14.6" />
      <line x1="8.6" y1="18" x2="13" y2="18" />
    </svg>
  );
}

// The Stories glyph: a folded paper with a masthead rule and two columns.
// Built like its neighbours — 24 box, currentColor, 2px strokes — so it sits
// in the row without redrawing the set.
function NewsIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5.5h13a1 1 0 0 1 1 1V19a1.5 1.5 0 0 0 1.5 1.5H5.5A1.5 1.5 0 0 1 4 19V5.5Z" />
      <line x1="7" y1="9" x2="14" y2="9" />
      <line x1="7" y1="12.5" x2="14" y2="12.5" />
      <line x1="7" y1="16" x2="11" y2="16" />
      <path d="M18 9.5h1.5a.5.5 0 0 1 .5.5V19a1.5 1.5 0 0 1-1.5 1.5" />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3.5" y1="20" x2="20.5" y2="20" />
      <rect x="5" y="12" width="3.2" height="6.2" rx="1" fill="currentColor" stroke="none" />
      <rect x="10.4" y="8" width="3.2" height="10.2" rx="1" fill="currentColor" stroke="none" />
      <rect x="15.8" y="4.6" width="3.2" height="13.6" rx="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GroupsIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="8.5" cy="8" r="3" fill="currentColor" stroke="none" />
      <path d="M2.8 19c0-3.1 2.6-5.2 5.7-5.2s5.7 2.1 5.7 5.2" />
      <circle cx="16.8" cy="8.8" r="2.3" fill="currentColor" stroke="none" />
      <path d="M15.4 14c2.5.3 4.4 2.3 4.4 5" />
    </svg>
  );
}

function PersonIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="currentColor" stroke="none" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </svg>
  );
}

// Second masthead line per route. Today shows "Today's Questions" rather than
// the date (mirrors the iOS "Today's Predict Six" masthead); the ET date only
// remains as the fallback for the chromeless /q share door.
const PAGE_TITLES = {
  '/': "Today's Questions",
  '/play': "Today's Questions",
  '/results': 'Results',
  '/stories': 'Stories',
  '/stats': 'Stats',
  '/groups': 'Games',
  '/profile': 'Profile',
  '/leaderboard': 'Leaderboard',
  '/coin-ledger': 'Coin Ledger',
};

const iosBannerWrap = {
  maxWidth: 'var(--maxw)',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  background: 'var(--card-bg)',
  borderBottom: '1px solid var(--border)',
};
const iosBannerGet = {
  flexShrink: 0,
  padding: '7px 14px',
  borderRadius: 999,
  background: 'var(--gold)',
  color: '#1a1a1a', // near-black on gold for AA contrast (audit A2)
  fontWeight: 800,
  fontSize: 13,
  textDecoration: 'none',
};
const iosBannerClose = {
  flexShrink: 0,
  width: 26,
  height: 26,
  borderRadius: 13,
  border: 'none',
  background: 'transparent',
  color: 'var(--secondary-text)',
  fontSize: 13,
  cursor: 'pointer',
  lineHeight: 1,
};
const navWrap = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  padding: '10px 12px calc(12px + env(safe-area-inset-bottom))',
  zIndex: 20,
  pointerEvents: 'none', // transparent gutters stay click-through; the pill re-enables
};
const navPill = {
  pointerEvents: 'auto',
  display: 'inline-flex',
  gap: 6,
  background: 'var(--card-bg)',
  border: '1px solid var(--border)',
  borderRadius: 30,
  padding: 6,
  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.28)',
};

function Unavailable({ errorCode }) {
  const isAnonDisabled = errorCode === 'auth/operation-not-allowed';
  // A session that TIMED OUT (the 'starting' watchdog) is the one case here
  // that a reload genuinely fixes most of the time — the handshake stalled
  // rather than failed — so that case gets a button instead of an instruction.
  const isTimeout = errorCode === 'session_timeout';
  return (
    <main className="ps-page">
      <div className="ps-card ps-center">
        <p style={{ fontWeight: 800, fontSize: 18, marginTop: 0 }}>
          {isTimeout ? 'That took too long' : 'Not quite live yet'}
        </p>
        <p className="ps-muted" style={{ lineHeight: 1.5 }}>
          {isAnonDisabled
            ? 'Anonymous play is being switched on. Check back shortly.'
            : isTimeout
              ? "Your session didn't finish starting. One tap usually sorts it."
              : 'We hit a snag starting your session. Please refresh in a moment.'}
        </p>
        {isTimeout && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 18px', borderRadius: 999, cursor: 'pointer',
              fontWeight: 800, fontSize: 14.5,
              color: 'var(--gold)', background: 'rgba(212,168,75,0.12)',
              border: '1px solid var(--gold)',
            }}
          >
            Try again
          </button>
        )}
        {import.meta.env.DEV && (
          <p
            className="ps-muted"
            style={{ fontSize: 12, marginBottom: 0, opacity: 0.7 }}
          >
            dev hint: {errorCode || 'unknown'}
          </p>
        )}
      </div>
    </main>
  );
}
